// biome-ignore-all lint/suspicious/noConsole: Console logging is needed for development debugging

import http from '@/lib/http';
import { cmsPagesEditorDraftPath } from '@/lib/routes';
import { useAiProposalsStore } from '@/stores/ai-proposals-store';
import { useAssetsStore } from '@/stores/assets-store';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { buildSectionPayload } from './autosave-section-payload';
import {
  type AutosaveBatchPayload,
  type AutosaveSaveResponse,
  autosaveContext,
  autosaveRuntimeState,
  markAutosaveSaved,
  remapPendingAutosaveActions,
} from './state';

export const autosaveClient = {
  async send(batch: AutosaveBatchPayload) {
    if (!batch.pageId || !batch.actions.length) {
      return;
    }

    if (import.meta.env.DEV) {
      console.groupCollapsed(`[Autosave] ${batch.actions.length} action(s)`);
      console.log('Page ID:', batch.pageId);
      console.log('Actions:', batch.actions);
      if (batch.attempt) {
        console.log('Attempt:', batch.attempt);
      }
      console.groupEnd();
    }

    try {
      const currentPage = usePageEditorStore.getState().getPageForAutosave();

      if (!currentPage?.slots) {
        console.info('[Autosave] No page in store, skipping autosave');
        return;
      }

      const metadata = currentPage.metadata || {};
      const mainSections = currentPage.slots.main.sections;
      const sectionsPayload = buildSectionPayload({
        actions: batch.actions,
        currentSections: mainSections,
      });

      if (import.meta.env.DEV) {
        console.groupCollapsed(`[Autosave] Sending ${sectionsPayload.length} section change(s)`);
        console.log('Payload:', sectionsPayload);
        console.groupEnd();
      }

      const response = await http.patch(cmsPagesEditorDraftPath(batch.pageId), {
        sections: sectionsPayload,
        metadata,
      } as Parameters<typeof http.patch>[1]);
      const responseData = (response.data || {}) as AutosaveSaveResponse;
      markAutosaveSaved({ response: responseData, pageId: batch.pageId });

      if (autosaveRuntimeState.pendingAssetStoreRefresh) {
        autosaveRuntimeState.pendingAssetStoreRefresh = false;
        useAssetsStore.getState().markStale();
      }

      if (
        (responseData.sectionsMapping || responseData.blocksMapping) &&
        autosaveContext.pageId === batch.pageId
      ) {
        const sectionsMapping = responseData.sectionsMapping || {};
        const blocksMapping = responseData.blocksMapping || {};

        remapPendingAutosaveActions({ sectionsMapping, blocksMapping });

        if (import.meta.env.DEV) {
          if (Object.keys(sectionsMapping).length > 0) {
            console.log('[Autosave] Draft created, remapping section IDs:', sectionsMapping);
          }
          if (Object.keys(blocksMapping).length > 0) {
            console.log('[Autosave] Remapping block IDs:', blocksMapping);
          }
        }

        // Draft was just created: remap all three systems that hold live-version IDs.
        // (1) Editor store sections/blocks — setPageForAutosaveRemap
        // (2) Proposal clientSnapshots — remapClientSnapshotsForDraft
        // (3) Proposals server state — loadProposals with the new pageVersionId
        usePageEditorStore.getState().setPageForAutosaveRemap(sectionsMapping, blocksMapping);

        const proposalsStore = useAiProposalsStore.getState();
        if (Object.keys(blocksMapping).length > 0) {
          proposalsStore.remapClientSnapshotsForDraft(blocksMapping);
        }
        if (proposalsStore.lastLoadParams && responseData.pageVersionId) {
          void proposalsStore.loadProposals(
            batch.pageId,
            responseData.pageVersionId,
            proposalsStore.lastLoadParams.threadId,
          );
        }
      }

      if (
        (responseData.sectionsMapping ||
          responseData.blocksMapping ||
          responseData.hasUnpublishedChanges) &&
        autosaveContext.pageId === batch.pageId
      ) {
        const pageEditorStore = usePageEditorStore.getState();
        const latestPage = pageEditorStore.page;
        if (latestPage && !latestPage.hasUnpublishedChanges) {
          pageEditorStore.setPage(
            { ...latestPage, hasUnpublishedChanges: true },
            { resetHistory: false },
          );
        }
      }
    } catch (error) {
      console.error('[Autosave] Failed to save:', error);
      throw error;
    }
  },
};
